/**
 * Purchase Rumios Modal - Buy Rumio packs with Stripe
 * Shows quantity-based packs (5/15/50 Rumios)
 */

import { X, Loader2, Crown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { useRumioPacks, usePurchaseRumios, useRumioBalance } from '../../hooks/useRumios';
import { UNIFIED_RUMIO_CONFIG, TIER_UNLOCK_COSTS, MYSTERY_TIERS } from '../../utils/constants';
import type { RumioPackType, MysteryTier } from '../../types';

interface Props {
  onClose: () => void;
  highlightType?: MysteryTier;
}

export default function PurchaseRumiosModal(props: Props) {
  const { onClose } = props;
  // props.highlightType can be used later to scroll to or highlight a specific tier
  const { t } = useLanguage();
  const { data: packsData, isLoading: isLoadingPacks } = useRumioPacks();
  const { data: balance } = useRumioBalance();
  const { mutate: purchase, isPending: isPurchasing } = usePurchaseRumios();

  const handlePurchase = (packType: RumioPackType) => {
    purchase(packType);
  };

  const packs = packsData?.packs || [];
  const userTier = packsData?.userTier || 'free';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        role="button"
        tabIndex={0}
        aria-label={t('modal.close', 'Close modal')}
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center mb-4 shadow-lg">
            <img src={UNIFIED_RUMIO_CONFIG.iconUrl} alt="" className="w-10 h-10" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">
            {t('purchaseRumios.title', 'Get Rumios')}
          </h2>
          <p className="text-gray-600 mt-1">
            {t('purchaseRumios.subtitle', 'Unlock mystery deals before everyone else')}
          </p>
        </div>

        {/* Current Balance */}
        {balance && (
          <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-4 mb-6 border border-amber-200">
            <div className="text-center">
              <p className="text-sm text-amber-600 mb-2">{t('purchaseRumios.yourBalance', 'Your Balance')}</p>
              <div className="flex items-center justify-center gap-2">
                <img src={UNIFIED_RUMIO_CONFIG.iconUrl} alt="" className="w-10 h-10" />
                <span className="text-4xl font-bold text-amber-700">{balance.balance}</span>
              </div>
              <p className="text-sm text-amber-600 mt-1">{UNIFIED_RUMIO_CONFIG.namePlural}</p>
            </div>
          </div>
        )}

        {/* Tier Unlock Guide */}
        <div className="bg-gray-50 rounded-xl p-4 mb-6">
          <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-3">
            {t('purchaseRumios.unlockCosts', 'Unlock Costs by Tier')}
          </p>
          <div className="grid grid-cols-3 gap-2">
            {(['super', 'fresh', 'standard'] as MysteryTier[]).map((tier) => {
              const tierConfig = MYSTERY_TIERS[tier];
              const cost = balance?.tier_costs?.[tier] ?? TIER_UNLOCK_COSTS[tier];
              return (
                <div key={tier} className="text-center p-2 bg-white rounded-lg border border-gray-200">
                  <span className="text-lg">{tierConfig.icon}</span>
                  <p className="text-xs font-medium text-gray-600">{tierConfig.name}</p>
                  <p className="text-sm font-bold text-gray-900 flex items-center justify-center gap-1">
                    {cost} <img src={UNIFIED_RUMIO_CONFIG.iconUrl} alt="" className="w-4 h-4 inline-block" />
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Loading State */}
        {isLoadingPacks && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          </div>
        )}

        {/* Packs */}
        {!isLoadingPacks && (
          <div className="space-y-4">
            {packs.map((pack, index) => {
              const isPopular = index === 1; // Value pack is usually the best deal
              const isAvailable = pack.available;

              return (
                <div
                  key={pack.type}
                  className={`relative rounded-xl border-2 p-4 transition-all ${
                    isPopular
                      ? 'border-amber-400 bg-amber-50 shadow-lg'
                      : 'border-gray-200 hover:border-gray-300'
                  } ${!isAvailable ? 'opacity-60' : ''}`}
                >
                  {/* Popular badge */}
                  {isPopular && (
                    <div className="absolute -top-3 left-4 px-2 py-0.5 bg-amber-500 text-white text-xs font-bold rounded-full">
                      {t('purchaseRumios.bestValue', 'Best Value')}
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
                        <img src={UNIFIED_RUMIO_CONFIG.iconUrl} alt="" className="w-7 h-7" />
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900">{pack.name}</h3>
                        <p className="text-sm text-amber-600 font-semibold">
                          {pack.rumios} {UNIFIED_RUMIO_CONFIG.namePlural}
                        </p>
                      </div>
                    </div>

                    <button
                      onClick={() => handlePurchase(pack.type)}
                      disabled={isPurchasing || !isAvailable}
                      className={`px-5 py-2.5 rounded-xl font-bold transition-colors ${
                        isAvailable
                          ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-white hover:from-amber-600 hover:to-amber-700 shadow-md'
                          : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      {isPurchasing ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        pack.price_formatted
                      )}
                    </button>
                  </div>

                  {/* Description */}
                  <p className="text-xs text-gray-500 mt-2">{pack.description}</p>
                </div>
              );
            })}
          </div>
        )}

        {/* Upgrade CTA */}
        {userTier !== 'pro_plus' && userTier !== 'pro+' && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="bg-gradient-to-r from-action-light/10 to-action-dark/10 rounded-xl p-4 text-center">
              <Crown className="w-6 h-6 mx-auto text-action mb-2" />
              <p className="font-medium text-gray-900">
                {t('purchaseRumios.upgradeTitle', 'Want unlimited early access?')}
              </p>
              <p className="text-sm text-gray-600 mb-3">
                {t('purchaseRumios.upgradeDescription', 'Pro+ members see all deals instantly')}
              </p>
              <Link
                to="/account/subscription"
                className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-action-light to-action-dark text-white font-bold rounded-lg hover:from-action to-action-dark transition-colors"
              >
                {t('purchaseRumios.upgradeButton', 'Upgrade to Pro+')}
              </Link>
            </div>
          </div>
        )}

        {/* Terms */}
        <p className="mt-4 text-xs text-center text-gray-400">
          {t('purchaseRumios.terms', 'Rumios never expire. Purchases are non-refundable.')}
        </p>
      </div>
    </div>
  );
}
